import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import igovttLogo from "../../assets/igovtt.png";
import PublicationsDocumentGrid, { libraryDocuments } from "./PublicationsDocumentGrid";
import "./PublicationsPage.css";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/issuer", label: "For Issuers" },
  { to: "/graduation", label: "For Credential Holders" },
  { to: "/verifier", label: "For Verifiers" },
  { to: "/publication", label: "Publication", active: true },
  { to: "/faq", label: "FAQs" },
];

const HelpDropdown = ({ helpOpen, setHelpOpen, menuOpen, setMenuOpen }) => {
  const wrapperRef = useRef(null);

  const handleHelpClick = () => {
    setHelpOpen((prev) => {
      const next = !prev;
      if (next && menuOpen) setMenuOpen(false);
      return next;
    });
  };

  const handleHelpKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleHelpClick();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) && helpOpen) {
        setHelpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [helpOpen, setHelpOpen]);

  return (
    <div className="vt-pub-help-wrapper" ref={wrapperRef}>
      <div
        className="vt-pub-help"
        role="button"
        tabIndex={0}
        onClick={handleHelpClick}
        onKeyDown={handleHelpKeyDown}
        aria-expanded={helpOpen}
        aria-haspopup="true"
        aria-controls="vtPubHelpDropdown"
        aria-label="Help menu"
      >
        <div className="vt-pub-flex-overflow-hidden">
          <div className="vt-pub-help-text">Help</div>
        </div>
        <div className="vt-pub-help-icon">
          <img src="https://t-t-prod-c3atgyatcbgvfdhc.a02.azurefd.net/images/down_arrow.svg" alt="Help dropdown" />
        </div>
      </div>
      <div id="vtPubHelpDropdown" className={`vt-pub-help-dropdown ${helpOpen ? "open" : ""}`} aria-label="Help dropdown">
        <div className="vt-pub-help-dropdown-inner">
          <div className="vt-pub-help-dropdown-item">
            <div className="vt-pub-help-dropdown-text vt-pub-help-dropdown-text--primary">About Us</div>
          </div>
          <div className="vt-pub-help-dropdown-item">
            <div className="vt-pub-help-dropdown-text">Terms &amp; Conditions</div>
          </div>
          <div className="vt-pub-help-dropdown-item">
            <div className="vt-pub-help-dropdown-text">Privacy Policy</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicationsLibraryPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const linkStyle = {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    alignItems: "center",
  };

  const handleMenuToggle = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (next) setHelpOpen(false);
      return next;
    });
  };

  return (
    <div style={{ margin: 0, padding: 0, background: "#F8FAFC", zoom: 0.97 }}>
      <div style={{ width: "100%", minHeight: "100%", background: "#F8FAFC", overflow: "visible", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-start", display: "inline-flex" }}>
        <div className="vt-header-publications" aria-label="Site header">
          <div className="vt-pub-header-inner">
            <div className="vt-pub-header-row">
              <div className="vt-pub-header-left">
                <div className="vt-pub-logo" aria-label="VerifyTT logo">
                  <Link to="/">
                    <img alt="VerifyTT logo" src="https://t-t-prod-c3atgyatcbgvfdhc.a02.azurefd.net/images/logo.svg" />
                  </Link>
                </div>
              </div>

              <div className="vt-pub-header-center">
                <div className="vt-pub-nav-desktop" aria-label="Primary navigation">
                  {NAV_LINKS.map(({ to, label, active }) => (
                    <div className="vt-pub-nav-item" key={to}>
                      <div className="vt-pub-nav-item-inner">
                        <Link to={to} style={linkStyle} aria-label={label}>
                          <div className={`vt-pub-nav-text${active ? " vt-pub-nav-text-active" : ""}`}>{label}</div>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="vt-pub-header-right">
                <HelpDropdown helpOpen={helpOpen} setHelpOpen={setHelpOpen} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
                <div className={`vt-pub-menu-toggle${menuOpen ? " open" : ""}`} onClick={handleMenuToggle} aria-label="Toggle navigation">
                  <div className="vt-pub-menu-toggle-line" />
                </div>
              </div>

              <div className={`vt-pub-nav-dropdown${menuOpen ? " open" : ""}`} id="pubNavDropdown" aria-label="Mobile navigation">
                {NAV_LINKS.map(({ to, label, active }) => (
                  <div className="vt-pub-nav-dropdown-item" key={to}>
                    <Link to={to} style={linkStyle} aria-label={label}>
                      <span className={`vt-pub-nav-dropdown-text${active ? " vt-pub-nav-dropdown-text-active" : ""}`}>{label}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <main className="flex w-full flex-col items-center px-4 pb-20 pt-6 sm:px-6 lg:px-8" aria-label="Publications library">
          <div className="w-full max-w-[1204px]">
            <nav aria-label="Breadcrumb" className="mb-7 flex flex-wrap items-center gap-2 text-[14px] font-normal">
              <Link to="/publication" className="text-[#333333] no-underline transition-colors hover:text-[#116DFD]">
                Publications
              </Link>
              <span aria-hidden="true" className="text-[#64748b]">›</span>
              <span className="text-[#116DFD]">Library</span>
            </nav>

            <section className="mb-12 mt-8 max-w-[720px]">
              <h1 className="m-0 text-[30px] font-bold leading-[1.15] text-[#101828]">
                Browse The Full Publications Library
              </h1>
              <p className="mt-4 mb-0 text-[16px] leading-7 text-[#475467]">
                Explore all reports, white papers, guides, and supporting resources in one central archive.
              </p>
            </section>

            <section className="pb-28" aria-label="Library documents list">
              <PublicationsDocumentGrid documents={libraryDocuments} ariaLabel="Full publications library documents" />
            </section>
          </div>
        </main>

        <div className="vt-footer" role="contentinfo" aria-label="Footer">
          <div className="vt-footer-inner">
            <div className="vt-footer-top">
              <div className="vt-footer-top-row" style={{ alignItems: "center", gap: "32px", flexWrap: "wrap", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "32px", flexWrap: "wrap" }}>
                  <div className="vt-footer-brand">
                    <div className="vt-footer-logo-row">
                      <div className="vt-footer-logo-wrapper" aria-label="VerifyTT logo">
                        <img alt="VerifyTT logo" src="https://t-t-prod-c3atgyatcbgvfdhc.a02.azurefd.net/images/logo.svg" />
                      </div>
                    </div>
                    <div className="vt-footer-tagline">Trusted Credentials. Verified.</div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                    <img
                      src={igovttLogo}
                      alt="iGovTT logo"
                      style={{ height: "76px", width: "auto", objectFit: "contain" }}
                    />
                  </div>
                </div>

                <div className="vt-footer-ministry">
                  An Initiative of the Ministry of Public Administration and Artificial Intelligence and The National Information and Communication Technology Company Limited.
                </div>
              </div>
            </div>

            <div className="vt-footer-divider-wrapper">
              <div className="vt-footer-divider"></div>
            </div>

            <div className="vt-footer-bottom">
              <div className="vt-footer-copy">©2025 VerifyTT · All rights reserved.</div>
              <div className="vt-footer-links" aria-label="Footer links">
                <div className="vt-footer-link-item">
                  <div className="vt-footer-link-inner">
                    <div className="vt-footer-link-text-wrapper">
                      <div className="vt-footer-link-text">Term of use</div>
                    </div>
                  </div>
                </div>
                <div className="vt-footer-link-item">
                  <div className="vt-footer-link-inner">
                    <div className="vt-footer-link-text-wrapper">
                      <div className="vt-footer-link-text">
                        <a aria-label="Privacy policy — opens in a new tab" href="https://dev-verifytt.gov.tt/privacy-policy.html" target="_blank" rel="noreferrer">
                          Privacy policy
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="vt-footer-link-item">
                  <div className="vt-footer-link-inner">
                    <div className="vt-footer-link-text-wrapper">
                      <div className="vt-footer-link-text">Help / Support</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationsLibraryPage;
